import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { MutationOrNetworkState } from "../../../common/models/NetworkRequest";
import { RequestWorkflowTab } from "./WorkflowSetupTabs";
import { usePrimaryOrganization, usePrimaryOrganizationId } from "../../hooks";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";
import {
  ProgressFooter,
  ProgressFooterActions,
  ProgressFooterSaveNotification,
} from "../get-compliant/ProgressFooter";
import { TopNavigationBreadcrumb } from "../../components/privacyCenter/TopNavigationBreadcrumb";
import { useOnboardingProgress } from "../../../common/hooks/useOnboardingProgress";
import { useWorkflowSetupNavItems, WorkflowSetupVerticalNav } from "./WorkflowSetupVerticalNav";
import { WorkflowHelpCard } from "./WorkflowInformationCard";
import {
  HorizontalStepProgressIndicator,
  HorizontalStepProgressStep,
} from "./HorizontalStepProgressIndicator";
import { useOrgRegulationRequestTypes } from "../../hooks/useOrgRegulationRequestTypes";
import { WorkflowType } from "../../../common/service/server/dto/RegulationRequestTypeDto";
import { useHistory } from "react-router";
import { useSurvey } from "../../../common/hooks/api/useSurvey";
import { DevInstructionsFinishedWorkflowSetupSlug } from "../stay-compliant/WorkflowsDashboardPage";
import { Routes } from "../../root/routes";
import { DEVELOPER_INSTRUCTIONS_SURVEY_NAME } from "../stay-compliant/PrivacyNoticeChecklistItems";
import { Api } from "../../../common/service/Api";
import { PageLoading } from "../../components";

export function WorkflowSetupPageWrapper({
  selectedTabKey,
  alwaysShowFooter,
  nextOnClick,
  nextContent,
  nextDisabled,
  nextDisabledTooltip,
  isSaving,
  saveRequest,
  children,
  hideFooter,
  contentAboveSteps,
  stepProgressSteps,
  onStepProgressClick,
}: {
  selectedTabKey: RequestWorkflowTab;
  alwaysShowFooter?: boolean;
  nextOnClick: () => void;
  nextContent?: string;
  nextDisabled: boolean;
  nextDisabledTooltip?: string;
  isSaving?: boolean;
  saveRequest?: MutationOrNetworkState | MutationOrNetworkState[];
  children: React.ReactNode;
  hideFooter?: boolean;
  /** Optional content rendered above horizontal step progress */
  contentAboveSteps?: React.ReactNode;
  /** Horizontal step progress indicator - each step can be clicked to scroll to that section */
  stepProgressSteps?: HorizontalStepProgressStep[];
  onStepProgressClick?: (stepId: string) => void;
}) {
  const history = useHistory();
  const queryClient = useQueryClient();
  const orgId = usePrimaryOrganizationId();
  const [organization] = usePrimaryOrganization();
  const [onboardingProgress] = useOnboardingProgress(orgId);
  const { filterByWorkflowType, loading: requestTypesLoading } = useOrgRegulationRequestTypes();
  const { setAnswer: setDevSurveyAnswer } = useSurvey(orgId, DEVELOPER_INSTRUCTIONS_SURVEY_NAME);

  const workflowTypeByTab: Partial<Record<RequestWorkflowTab, WorkflowType>> = {
    "access-request": WorkflowType.KNOW,
    "request-to-delete": WorkflowType.DELETE,
    "correction-request": WorkflowType.CORRECTION,
    "appeal-request": WorkflowType.APPEAL,
    "withdrawal-of-consent-request": WorkflowType.WITHDRAW_CONSENT,
    "profiling-information-request": WorkflowType.PROFILING_INFORMATION,
    "portability-request": WorkflowType.PORTABILITY,
    "objection-request": WorkflowType.OBJECTION,
    "restrict-processing-request": WorkflowType.RESTRICT_PROCESSING,
    "request-to-opt-out": WorkflowType.OPT_OUT,
    "request-to-limit": WorkflowType.LIMIT,
    "request-to-profiling-opt-out": WorkflowType.PROFILING_OPT_OUT,
  };

  const workflowType = workflowTypeByTab[selectedTabKey];
  const workflowRequestTypes = workflowType ? filterByWorkflowType(workflowType) : [];

  const {
    workflowsSetupOnboardingProgress: {
      isFinishedWorkflowSetupOnboarding = false,
      isLimitSettingsComplete = false,
    },
  } = onboardingProgress || {};

  const hasRequestTypesForWorkflow = (workflowType: WorkflowType) =>
    requestTypesLoading ? false : filterByWorkflowType(workflowType).length > 0;

  const navItems = useWorkflowSetupNavItems({
    selectedTabKey,
    hasRequestTypesForWorkflow,
    isLimitStepIncomplete: !isLimitSettingsComplete && isFinishedWorkflowSetupOnboarding,
    isFinishedWorkflowSetupOnboarding,
  });
  const selectedTabIndex = navItems.findIndex((item) => item.tabKey === selectedTabKey);
  const nextNavItem = selectedTabIndex >= 0 ? navItems[selectedTabIndex + 1] : undefined;
  const isNavigationReady = !requestTypesLoading;

  const handleNextClick = async () => {
    if (!isNavigationReady) {
      return;
    }

    if (nextNavItem) {
      history.push(nextNavItem.route);
      return;
    }

    try {
      await nextOnClick();
    } catch (error) {
      // Do not block completion of the onboarding step when a final-page
      // save action fails; those pages already support their own save flows.
      console.error(error);
    }
    // Because DSAR Workflows is currently the penultimate onboarding step, also mark the org as being permanently done with onboarding (this also means that if DSAR Workflows is ever re-ordered across onboarding steps, this API update call will need to be moved to whatever the new last/second-to-last onboarding step is)
    // Persist the latch first to avoid a scenario where the survey answer is written but the update org request fails, which would prevent the footer from being shown again, making it impossible for the org to set the latch
    await Api.organization.updateOrganization(orgId, {
      name: organization.name,
      logoUrl: organization.logoUrl,
      regulations: organization.regulations,
      finishedOnboardingLatch: true,
    });
    await setDevSurveyAnswer(DevInstructionsFinishedWorkflowSetupSlug, "true");
    // Ensure dashboard/nav gating reads the updated completion state right away.
    await queryClient.invalidateQueries(["orgOnboardingProgress", orgId]);
    await queryClient.invalidateQueries(["organization", orgId]);
    history.push(Routes.workflows.dashboard);
  };

  const resolvedNextContent = nextNavItem ? "Next" : nextContent || "Done with Workflow Setup";
  const resolvedNextDisabled = nextDisabled || !isNavigationReady;

  if (!organization) {
    return <PageLoading />;
  }

  return (
    <StandardPageWrapper
      fullWidth
      footer={
        (!isFinishedWorkflowSetupOnboarding || alwaysShowFooter) && (
          <ProgressFooter
            content={isSaving && <ProgressFooterSaveNotification request={saveRequest} />}
            actions={
              hideFooter !== true && (
                <ProgressFooterActions
                  nextContent={resolvedNextContent}
                  nextOnClick={handleNextClick}
                  nextDisabled={resolvedNextDisabled}
                  nextDisabledTooltip={nextDisabledTooltip}
                />
              )
            }
          />
        )
      }
    >
      <div className="tw-boundary" style={{ marginBottom: "32px" }}>
        <TopNavigationBreadcrumb title="Workflow Setup" source="DSAR Workflows" />

        <div className="flex gap-8 mt-6">
          <WorkflowSetupVerticalNav
            selectedTabKey={selectedTabKey}
            hasRequestTypesForWorkflow={hasRequestTypesForWorkflow}
            isLimitStepIncomplete={!isLimitSettingsComplete && isFinishedWorkflowSetupOnboarding}
            isFinishedWorkflowSetupOnboarding={isFinishedWorkflowSetupOnboarding}
          />
          <div className="flex-1 min-w-0 max-w-[776px]">
            {workflowType && (
              <WorkflowHelpCard
                workflowType={workflowType}
                requestTypes={workflowRequestTypes}
                organization={organization ?? null}
              />
            )}
            {contentAboveSteps}
            {stepProgressSteps && stepProgressSteps.length > 0 && (
              <HorizontalStepProgressIndicator
                steps={stepProgressSteps}
                onStepClick={onStepProgressClick}
              />
            )}
            {children}
          </div>
        </div>
      </div>
    </StandardPageWrapper>
  );
}
